import { useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router";
import { marked } from "zustand-querystring/format/marked";
import { localMarketSuggestions } from "~/components/marketSearch/marketSearchSelections";
import type { MarketSearchSelection } from "~/components/marketSearch/MarketSearchBar";
import {
  initialDiscoveryV2SearchStoreState,
  useDiscoveryV2SearchStore,
} from "../v2/DiscoveryV2SearchStore";
type SearchState = typeof initialDiscoveryV2SearchStoreState;
export const stateKeys = Object.keys(
  initialDiscoveryV2SearchStoreState,
) as (keyof SearchState)[];

// Keep the existing marked URL format so bookmarks and both results versions agree.
const readSearchState = (search: string): SearchState => {
  const params = new URLSearchParams(search);
  const managedParams: Record<string, string[]> = {};
  for (const key of stateKeys) {
    if (params.has(key)) {
      // The marked parser performs its own URI decoding.
      managedParams[key] = search
        .replace(/^\?/, "")
        .split("&")
        .filter((part) => part.startsWith(`${key}=`))
        .map((part) => part.slice(key.length + 1));
    }
  }
  try {
    return {
      ...initialDiscoveryV2SearchStoreState,
      ...marked.parseStandalone(managedParams, {
        initialState: initialDiscoveryV2SearchStoreState,
      }),
    };
  } catch {
    return { ...initialDiscoveryV2SearchStoreState };
  }
};

export const useDiscoverySearchUrlState = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);
    // Legacy entry points navigate after updating the store without forwarding a query.
    if (navigationType === "POP" || stateKeys.some((key) => params.has(key))) {
      useDiscoveryV2SearchStore.setState(readSearchState(location.search));
    }
  }, [location.key, location.search, navigationType]);
};

export const applyMarketSearchSelection = (
  selection: MarketSearchSelection,
  current: SearchState,
): SearchState => {
  const next = {
    ...current,
    searchValue: "",
    selectedProductId: undefined,
    selectedProductParentId: undefined,
    menuActive: false,
  };
  switch (selection.kind) {
    case "text":
      return {
        ...next,
        searchValue: selection.value,
        isMySuppliersOnly: selection.scope === "suppliers",
      };
    case "supplier":
      return {
        ...next,
        selectedSuppliers: [
          { id: selection.supplierId, name: selection.label },
        ],
      };
    case "product":
      return {
        ...next,
        selectedSuppliers: [
          { id: selection.supplierId, name: selection.supplierName },
        ],
        selectedProductId: selection.productItemId,
        selectedProductParentId: selection.parentProductId,
      };
    case "category":
      return {
        ...next,
        categoryKey: selection.categoryKey,
        styleSearchValues: current.styleSearchValues.filter((value) =>
          localMarketSuggestions.some(
            (option) =>
              option.kind === "style" &&
              option.categoryKey === selection.categoryKey &&
              option.value === value,
          ),
        ),
      };
    case "style":
      return {
        ...next,
        categoryKey: selection.categoryKey,
        styleSearchValues: [selection.value],
      };
    case "container":
      return { ...next, selectedContainerTypes: [selection.value] };
    case "dietary":
      return { ...next, selectedDietaryTypes: [selection.value] };
    case "filterQuery":
      return {
        ...next,
        categoryKey: selection.categoryKey,
        styleSearchValues: selection.styleSearchValues,
        selectedContainerTypes: [selection.containerType],
      };
    case "filters":
      return {
        ...next,
        categoryKey: "beer",
        styleSearchValues: [
          selection.preset === "neipaKeg" ? "neipa" : "stout",
        ],
        selectedContainerTypes: [
          selection.preset === "neipaKeg" ? "keg" : "can",
        ],
        searchValue: selection.preset === "neipaKeg" ? "" : "imperial",
      };
  }
};
